import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Skull,
  Bug,
  Target as TargetIcon,
  AlertOctagon,
  FileText,
  X,
} from "lucide-react";

/* ─── Size presets ─── */
// Compact is the sidebar-panel sizing; roomy is the full-page sizing.
// When used on a larger page, pass size="roomy" to get bigger circles,
// longer label lines, more lane spacing, and a bigger top title.
const SIZE_PRESETS = {
  compact: {
    CIRCLE_SIZE: 44,
    ACTOR_CIRCLE_SIZE: 52,
    ICON_SIZE: 20,
    ACTOR_ICON_SIZE: 26,
    LABEL_MAX_WIDTH: 130,
    LABEL_FONT_PX: 11,
    LANE_GAP: 28,
    ROW_SPACING_MIN: 84,
    PADDING_X: 32,
    PADDING_Y: 28,
    TITLE_PX: 12,
    CONTROL_BTN: "h-6 w-6",
    CONTROL_ICON: 14,
    EDGE_LABEL_PX: 9,
  },
  roomy: {
    CIRCLE_SIZE: 64,
    ACTOR_CIRCLE_SIZE: 76,
    ICON_SIZE: 30,
    ACTOR_ICON_SIZE: 38,
    LABEL_MAX_WIDTH: 190,
    LABEL_FONT_PX: 13,
    LANE_GAP: 56,
    ROW_SPACING_MIN: 128,
    PADDING_X: 56,
    PADDING_Y: 48,
    TITLE_PX: 15,
    CONTROL_BTN: "h-8 w-8",
    CONTROL_ICON: 16,
    EDGE_LABEL_PX: 11,
  },
};

const AUTO_ADVANCE_MS = 10000;

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.06;
const FIT_MARGIN = 20;
const NODE_LABEL_BLOCK_HEIGHT = 30;

/* ─── Lanes ─── */
const LANES = ["ACTOR", "METHOD", "TARGET", "CONSEQUENCE"];
const LANE_ICONS = {
  ACTOR: Skull,
  METHOD: Bug,
  TARGET: TargetIcon,
  CONSEQUENCE: AlertOctagon,
};

/* ─── Verb families ─── */
const ATTACK_VERBS =
  /compromis|weaponiz|exploit|attack|hack|steal|phish|inject|breach|maliciou|fraud|bypass|weapon/i;
const IMPACT_VERBS =
  /enable|allow|trigger|cause|lead|affect|expos|result|achieve|prompt/i;
const IDENTITY_VERBS = /\b(is|is a|tracked as|based on|part of|linked to)\b/i;

const CVE_PATTERN = /^CVE[-\s]?\d{4}/i;
const ACTOR_TERMS = /\b(attacker|hacker|threat|adversar|actor|group|team)/i;
const METHOD_TERMS =
  /\b(vulnerability|cve|flaw|exploit|rat|trojan|malware|implant|backdoor|rootkit|payload|ransomware|sdk|package|image|campaign|phish|sql injection|scam|schem)/i;
const TARGET_TERMS =
  /\b(server|system|appliance|account|token|credential|repository|github|npm|pypi|docker|kubernetes|api|endpoint|user|customer|taxpayer|organization|developer|employee|database|infrastructure)/i;
const CONSEQUENCE_TERMS =
  /\b(execution|compromise|exfiltration|theft|access|escalation|damage|loss|attack|breach|disclosure|leak|recommendations|patching)/i;

const isIdentityVerb = (v) => !!v && IDENTITY_VERBS.test(v);

/* ─── Lane classification ─── */
function classifyLanes({ nodes, links }) {
  const inbound = Object.fromEntries(nodes.map((n) => [n, []]));
  const outbound = Object.fromEntries(nodes.map((n) => [n, []]));
  links.forEach((l) => {
    if (outbound[l.source]) outbound[l.source].push(l);
    if (inbound[l.target]) inbound[l.target].push(l);
  });

  const lanes = {};

  nodes.forEach((n) => {
    const outs = outbound[n];
    const ins = inbound[n];

    if (CVE_PATTERN.test(n)) {
      lanes[n] = "METHOD";
      return;
    }
    const attacksOut = outs.some((l) => ATTACK_VERBS.test(l.type));
    const attackedIn = ins.some((l) => ATTACK_VERBS.test(l.type));
    if (attacksOut && !attackedIn) {
      lanes[n] = "ACTOR";
      return;
    }
    if (ACTOR_TERMS.test(n) && !attackedIn) {
      lanes[n] = "ACTOR";
      return;
    }
    if (outs.length === 0 && ins.length > 0) {
      lanes[n] = "CONSEQUENCE";
      return;
    }
    if (CONSEQUENCE_TERMS.test(n) && outs.length === 0) {
      lanes[n] = "CONSEQUENCE";
      return;
    }
    const weaponized = ins.some(
      (l) => /weaponiz|tracked as|is\b/i.test(l.type) || /use/i.test(l.type),
    );
    if (weaponized && METHOD_TERMS.test(n)) {
      lanes[n] = "METHOD";
      return;
    }
    if (METHOD_TERMS.test(n)) {
      lanes[n] = "METHOD";
      return;
    }
    if (attackedIn) {
      lanes[n] = "TARGET";
      return;
    }
    if (TARGET_TERMS.test(n)) {
      lanes[n] = "TARGET";
      return;
    }
    lanes[n] = null;
  });

  const laneRank = { ACTOR: 0, METHOD: 1, TARGET: 2, CONSEQUENCE: 3 };
  let changed = true;
  let iterations = 0;
  while (changed && iterations < 5) {
    changed = false;
    iterations++;
    nodes.forEach((n) => {
      if (lanes[n]) return;
      const upstream = inbound[n].map((l) => lanes[l.source]).filter(Boolean);
      const downstream = outbound[n]
        .map((l) => lanes[l.target])
        .filter(Boolean);
      if (upstream.length === 0 && downstream.length === 0) return;
      const maxUp =
        upstream.length > 0
          ? Math.max(...upstream.map((l) => laneRank[l]))
          : -1;
      const minDown =
        downstream.length > 0
          ? Math.min(...downstream.map((l) => laneRank[l]))
          : 4;
      let candidate;
      if (maxUp >= 0 && maxUp < 3) candidate = maxUp + 1;
      else if (minDown <= 3 && minDown > 0) candidate = minDown - 1;
      else if (maxUp >= 0) candidate = maxUp;
      else candidate = minDown;
      candidate = Math.max(0, Math.min(3, candidate));
      lanes[n] = LANES[candidate];
      changed = true;
    });
  }

  nodes.forEach((n) => {
    if (!lanes[n]) {
      if (inbound[n].length === 0 && outbound[n].length === 0) {
        lanes[n] = "METHOD";
      } else {
        lanes[n] = outbound[n].length > inbound[n].length ? "METHOD" : "TARGET";
      }
    }
  });

  return lanes;
}

/* ─── Text measurement (cached per font size) ─── */
const measureCtxCache = {};
const getMeasureCtx = (fontPx) => {
  if (typeof document === "undefined") return null;
  if (!measureCtxCache[fontPx]) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = `500 ${fontPx}px Poppins, ui-sans-serif, system-ui, sans-serif`;
    measureCtxCache[fontPx] = ctx;
  }
  return measureCtxCache[fontPx];
};
const measureText = (text, fontPx = 11) => {
  const ctx = getMeasureCtx(fontPx);
  if (!ctx) return text.length * (fontPx * 0.59);
  return ctx.measureText(text).width;
};
const bestWrappedWidth = (name, cap, fontPx) => {
  const full = measureText(name, fontPx);
  if (full <= cap) return full;
  const words = name.split(/\s+/);
  if (words.length === 1) return full;
  let best = full;
  for (let i = 1; i < words.length; i++) {
    const l1 = measureText(words.slice(0, i).join(" "), fontPx);
    const l2 = measureText(words.slice(i).join(" "), fontPx);
    const w = Math.max(l1, l2);
    if (w < best) best = w;
  }
  return best;
};

/* ─── Relative time ─── */
const relativeTime = (unix) => {
  if (!unix) return "";
  const diff = Math.floor(Date.now() / 1000) - unix;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 604800)}w ago`;
};

/* ─── Layout (container-independent) ─── */
function layoutGraph(graphObj, sz) {
  if (!graphObj?.nodes?.length || !Array.isArray(graphObj?.links)) {
    return {
      nodes: [],
      links: [],
      bbox: null,
      contentWidth: 0,
      contentHeight: 0,
    };
  }
  const { nodes: nodeNames, links } = graphObj;
  const laneByName = classifyLanes(graphObj);

  const byLane = { ACTOR: [], METHOD: [], TARGET: [], CONSEQUENCE: [] };
  nodeNames.forEach((n) => byLane[laneByName[n]].push(n));
  const activeLanes = LANES.filter((l) => byLane[l].length > 0);

  if (activeLanes.length === 0) {
    return {
      nodes: [],
      links: [],
      bbox: null,
      contentWidth: 0,
      contentHeight: 0,
    };
  }

  const nodeDesiredWidths = {};
  nodeNames.forEach((name) => {
    const wrapped = Math.min(
      bestWrappedWidth(name, sz.LABEL_MAX_WIDTH, sz.LABEL_FONT_PX),
      sz.LABEL_MAX_WIDTH,
    );
    const circleSize =
      laneByName[name] === "ACTOR" ? sz.ACTOR_CIRCLE_SIZE : sz.CIRCLE_SIZE;
    nodeDesiredWidths[name] = Math.max(circleSize, Math.ceil(wrapped));
  });

  const laneWidths = activeLanes.map((lane) =>
    byLane[lane].reduce(
      (m, n) => Math.max(m, nodeDesiredWidths[n]),
      sz.CIRCLE_SIZE,
    ),
  );

  let cursor = sz.PADDING_X;
  const laneCenters = activeLanes.map((_, i) => {
    const center = cursor + laneWidths[i] / 2;
    cursor += laneWidths[i] + sz.LANE_GAP;
    return center;
  });
  const contentWidth = cursor - sz.LANE_GAP + sz.PADDING_X;

  const tallestLane = Math.max(...activeLanes.map((l) => byLane[l].length));
  const contentHeight =
    sz.PADDING_Y * 2 + Math.max(1, tallestLane) * sz.ROW_SPACING_MIN;
  const centerY = contentHeight / 2;

  const placed = {};
  activeLanes.forEach((lane, laneIdx) => {
    const laneNodes = byLane[lane];
    const count = laneNodes.length;
    const x = laneCenters[laneIdx];
    const laneWidth = laneWidths[laneIdx];
    const laneHeight = count * sz.ROW_SPACING_MIN;
    const startY = centerY - laneHeight / 2 + sz.ROW_SPACING_MIN / 2;
    laneNodes.forEach((name, idx) => {
      placed[name] = {
        name,
        x,
        y: startY + idx * sz.ROW_SPACING_MIN,
        lane,
        slotWidth: laneWidth,
        circleSize: lane === "ACTOR" ? sz.ACTOR_CIRCLE_SIZE : sz.CIRCLE_SIZE,
      };
    });
  });

  const positionedLinks = links
    .map((l) => ({
      source: placed[l.source],
      target: placed[l.target],
      type: l.type,
      dashed: isIdentityVerb(l.type),
    }))
    .filter((l) => l.source && l.target);

  const nodesArr = Object.values(placed);
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  nodesArr.forEach((n) => {
    const halfW = Math.max(n.slotWidth, n.circleSize) / 2;
    const topY = n.y - n.circleSize / 2;
    const bottomY = n.y + n.circleSize / 2 + 6 + NODE_LABEL_BLOCK_HEIGHT;
    if (n.x - halfW < minX) minX = n.x - halfW;
    if (n.x + halfW > maxX) maxX = n.x + halfW;
    if (topY < minY) minY = topY;
    if (bottomY > maxY) maxY = bottomY;
  });
  const bbox = {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };

  return {
    nodes: nodesArr,
    links: positionedLinks,
    bbox,
    contentWidth,
    contentHeight,
  };
}

/* ─── Marquee label ─── */
const MarqueeLabel = ({ text, maxWidth, dark, sz }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [distance, setDistance] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;
    const check = () => {
      const textWidth = textEl.scrollWidth;
      const containerWidth = container.clientWidth;
      setDistance(
        textWidth > containerWidth + 1 ? textWidth - containerWidth : 0,
      );
    };
    check();
    const obs = new ResizeObserver(check);
    obs.observe(container);
    return () => obs.disconnect();
  }, [text]);

  const overflows = distance > 0;
  const scrollMs = Math.max(2000, (distance / 60) * 1000);
  const pauseMs = 1500;
  const totalMs = scrollMs + pauseMs * 2;
  const pauseStartPct = (pauseMs / totalMs) * 100;
  const scrollEndPct = ((pauseMs + scrollMs) / totalMs) * 100;

  const labelColor = dark ? "text-white" : "text-mainText";

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden"
      style={{
        maxWidth,
        maskImage: overflows
          ? "linear-gradient(to right, transparent 0, black 6px, black calc(100% - 6px), transparent 100%)"
          : undefined,
        WebkitMaskImage: overflows
          ? "linear-gradient(to right, transparent 0, black 6px, black calc(100% - 6px), transparent 100%)"
          : undefined,
      }}
    >
      <div
        ref={textRef}
        className={`font-poppins font-medium leading-tight ${labelColor}`}
        style={
          overflows
            ? {
                fontSize: sz.LABEL_FONT_PX,
                display: "inline-block",
                whiteSpace: "nowrap",
                animation: `cve-marquee-${Math.round(distance)} ${totalMs}ms linear infinite`,
              }
            : {
                fontSize: sz.LABEL_FONT_PX,
                textAlign: "center",
                whiteSpace: "normal",
                wordBreak: "normal",
                overflowWrap: "break-word",
              }
        }
      >
        {text}
      </div>
      {overflows && (
        <style>{`
          @keyframes cve-marquee-${Math.round(distance)} {
            0% { transform: translateX(0); }
            ${pauseStartPct.toFixed(2)}% { transform: translateX(0); }
            ${scrollEndPct.toFixed(2)}% { transform: translateX(-${distance}px); }
            100% { transform: translateX(-${distance}px); }
          }
        `}</style>
      )}
    </div>
  );
};

/* ─── Graph renderer ─── */
const Graph = ({ layout, dark, sz }) => {
  if (!layout.nodes.length) return null;

  const EDGE_COLOR = dark ? "rgba(56,189,248,0.55)" : "rgba(71,85,105,0.55)";
  const LABEL_BG = dark ? "rgba(14,19,28,0.92)" : "rgba(228,234,242,0.95)";
  const arrowId = `cve-arrow-${dark ? "d" : "l"}`;

  // Single uniform border across every node
  const NODE_BORDER = dark ? "rgba(14,165,233,0.55)" : "rgba(120,134,152,0.65)";
  const NODE_BG = dark ? "rgba(14,165,233,0.10)" : "rgba(84,98,118,0.08)";

  return (
    <>
      <svg
        className="pointer-events-none absolute inset-0"
        width={layout.contentWidth}
        height={layout.contentHeight}
        style={{ overflow: "visible" }}
      >
        <defs>
          <marker
            id={arrowId}
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={EDGE_COLOR} />
          </marker>
        </defs>
        {layout.links.map((link, i) => {
          const midX = (link.source.x + link.target.x) / 2;
          const midY = (link.source.y + link.target.y) / 2;
          const showLabel = link.type && link.type.length <= 22;
          return (
            <g key={i}>
              <line
                x1={link.source.x}
                y1={link.source.y}
                x2={link.target.x}
                y2={link.target.y}
                stroke={EDGE_COLOR}
                strokeWidth={1.5}
                strokeDasharray={link.dashed ? "4 4" : undefined}
                markerEnd={`url(#${arrowId})`}
              />
              {showLabel && (
                <g>
                  <rect
                    x={midX - measureText(link.type, sz.EDGE_LABEL_PX) / 2 - 4}
                    y={midY - sz.EDGE_LABEL_PX / 2 - 2}
                    width={measureText(link.type, sz.EDGE_LABEL_PX) + 8}
                    height={sz.EDGE_LABEL_PX + 4}
                    rx={3}
                    fill={LABEL_BG}
                  />
                  <text
                    x={midX}
                    y={midY + sz.EDGE_LABEL_PX / 2 - 1}
                    textAnchor="middle"
                    className="font-poppins"
                    style={{
                      fontSize: sz.EDGE_LABEL_PX,
                      fill: EDGE_COLOR,
                      fontWeight: 600,
                    }}
                  >
                    {link.type}
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>

      {layout.nodes.map((node) => {
        const isActor = node.lane === "ACTOR";
        const isCVE = CVE_PATTERN.test(node.name);

        return (
          <div
            key={node.name}
            title={node.name}
            className="absolute flex flex-col items-center gap-1.5 font-poppins"
            style={{
              left: node.x,
              top: node.y,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div
              className="flex shrink-0 items-center justify-center rounded-full backdrop-blur-sm"
              style={{
                width: node.circleSize,
                height: node.circleSize,
                border: `1px solid ${NODE_BORDER}`,
                background: NODE_BG,
              }}
            >
              {(() => {
                const iconSize = isActor ? sz.ACTOR_ICON_SIZE : sz.ICON_SIZE;
                const iconProps = {
                  size: iconSize,
                  strokeWidth: 2,
                  color: "white",
                  "aria-hidden": true,
                };
                if (isCVE) return <Bug {...iconProps} />;
                const LaneIcon = LANE_ICONS[node.lane];
                return <LaneIcon {...iconProps} />;
              })()}
            </div>
            {isCVE ? (
              <div
                className={`font-poppins font-mono font-medium ${dark ? "text-white" : "text-mainText"}`}
                style={{
                  maxWidth: node.slotWidth,
                  textAlign: "center",
                  fontSize: sz.LABEL_FONT_PX - 1,
                }}
              >
                {node.name}
              </div>
            ) : (
              <MarqueeLabel
                text={node.name}
                maxWidth={node.slotWidth}
                dark={dark}
                sz={sz}
              />
            )}
          </div>
        );
      })}
    </>
  );
};

/* ─── Pan/Zoom stage ─── */
const PanZoomStage = ({
  containerWidth,
  containerHeight,
  contentWidth,
  contentHeight,
  onInteract,
  dark,
  children,
}) => {
  const fitZoom = useMemo(() => {
    if (!contentWidth || !contentHeight) return 1;
    const availW = containerWidth - FIT_MARGIN * 2;
    const availH = containerHeight - FIT_MARGIN * 2;
    const scaleX = availW / contentWidth;
    const scaleY = availH / contentHeight;
    return Math.max(0.1, Math.min(1, Math.min(scaleX, scaleY)));
  }, [containerWidth, containerHeight, contentWidth, contentHeight]);

  const [zoom, setZoom] = useState(fitZoom);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const surfaceRef = useRef(null);

  useEffect(() => {
    setZoom(fitZoom);
    setPan({ x: 0, y: 0 });
  }, [fitZoom, contentWidth, contentHeight]);

  const clampPan = useCallback(
    (nextPan, nextZoom) => {
      if (!contentWidth || !contentHeight) return nextPan;
      const scaledW = contentWidth * nextZoom;
      const scaledH = contentHeight * nextZoom;
      const slackX = Math.max(0, (scaledW - containerWidth) / 2);
      const slackY = Math.max(0, (scaledH - containerHeight) / 2);
      const extraX = containerWidth * 0.15;
      const extraY = containerHeight * 0.15;
      const maxX = slackX + extraX;
      const maxY = slackY + extraY;
      return {
        x: Math.max(-maxX, Math.min(maxX, nextPan.x)),
        y: Math.max(-maxY, Math.min(maxY, nextPan.y)),
      };
    },
    [contentWidth, contentHeight, containerWidth, containerHeight],
  );

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      const cursorX = e.clientX - rect.left - containerWidth / 2;
      const cursorY = e.clientY - rect.top - containerHeight / 2;
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      const nextZoom = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, zoom * (1 + delta)),
      );
      if (nextZoom === zoom) return;
      const scale = nextZoom / zoom;
      const nextPan = {
        x: cursorX - (cursorX - pan.x) * scale,
        y: cursorY - (cursorY - pan.y) * scale,
      };
      setZoom(nextZoom);
      setPan(clampPan(nextPan, nextZoom));
      onInteract?.();
    },
    [zoom, pan, containerWidth, containerHeight, clampPan, onInteract],
  );

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    };
  };
  const handlePointerMove = (e) => {
    if (!isDragging || !dragStartRef.current) return;
    const dx = e.clientX - dragStartRef.current.clientX;
    const dy = e.clientY - dragStartRef.current.clientY;
    if (!dragStartRef.current.moved && Math.hypot(dx, dy) > 3) {
      dragStartRef.current.moved = true;
      onInteract?.();
    }
    setPan(
      clampPan(
        {
          x: dragStartRef.current.panX + dx,
          y: dragStartRef.current.panY + dy,
        },
        zoom,
      ),
    );
  };
  const handlePointerUp = (e) => {
    if (!isDragging) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {
      /* ignore */
    }
    setIsDragging(false);
    dragStartRef.current = null;
  };
  const handleDoubleClick = () => {
    setZoom(fitZoom);
    setPan({ x: 0, y: 0 });
    onInteract?.();
  };

  const offsetX = (containerWidth - contentWidth) / 2 + pan.x;
  const offsetY = (containerHeight - contentHeight) / 2 + pan.y;

  const cursor = isDragging ? "grabbing" : "grab";
  const isAtFit =
    Math.abs(zoom - fitZoom) < 0.001 && pan.x === 0 && pan.y === 0;

  const zoomBadgeClasses = dark
    ? "border-velBlue/30 bg-velBlue/[0.08] text-velBlue"
    : "border-surfaceBorder bg-surface/90 text-mutedText";

  return (
    <div
      ref={surfaceRef}
      className="absolute inset-0 overflow-hidden"
      style={{ cursor, touchAction: "none" }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDoubleClick={handleDoubleClick}
    >
      <div
        style={{
          position: "absolute",
          left: offsetX,
          top: offsetY,
          width: contentWidth,
          height: contentHeight,
          transform: `scale(${zoom})`,
          transformOrigin: "center center",
        }}
      >
        {children}
      </div>

      {!isAtFit && (
        <div
          className={`pointer-events-none absolute left-3 top-3 rounded-md border px-2 py-0.5 font-poppins font-mono text-[10px] backdrop-blur-sm ${zoomBadgeClasses}`}
        >
          {Math.round(zoom * 100)}%
        </div>
      )}
    </div>
  );
};

/* ─── Summary overlay ─── */
const SummaryOverlay = ({ report, onClose, dark, size }) => {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const paragraphs = (report || "").split(/\n\n+/).filter(Boolean);
  const eyebrowColor = dark ? "text-velBlue" : "text-mutedText";

  const bodyPx = size === "roomy" ? 13 : 11;
  const paragraphGap = size === "roomy" ? 14 : 10;

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col font-poppins"
      style={{
        background: dark ? "rgba(14,19,28,0.65)" : "rgba(213,221,232,0.65)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-mainDivider px-4 py-2">
        <div className="flex items-center gap-1.5">
          <FileText
            size={11}
            strokeWidth={2}
            className={eyebrowColor}
            aria-hidden
          />
          <span
            className={`text-[9px] font-semibold uppercase tracking-[0.14em] ${eyebrowColor}`}
          >
            Summary
          </span>
        </div>
        <button
          onClick={onClose}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-mutedText transition cursor-pointer hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2"
          title="Close summary"
          aria-label="Close summary"
        >
          <X size={13} strokeWidth={2} aria-hidden />
        </button>
      </div>

      <div
        className="flex-1 overflow-y-auto px-4 py-3"
        style={{ scrollbarWidth: "thin" }}
      >
        {paragraphs.length > 0 ? (
          paragraphs.map((p, i) => (
            <p
              key={i}
              className="font-poppins leading-relaxed text-mainText"
              style={{
                fontSize: bodyPx,
                marginBottom: i === paragraphs.length - 1 ? 0 : paragraphGap,
              }}
            >
              {p}
            </p>
          ))
        ) : (
          <p className="font-poppins text-[11px] text-mutedText">
            No summary available for this story.
          </p>
        )}
      </div>
    </div>
  );
};

/* ─── Panel ─── */
const CVEGraphPanel = ({
  data,
  dark = false,
  /**
   * "compact" (default) — sidebar-sized (sidebars, small cards)
   * "roomy"             — large-view sized (dedicated page / modal)
   */
  size = "compact",
  /**
   * Optional: override specific sizing tokens per-instance without forking
   * the SIZE_PRESETS dictionary. e.g. <CVEGraphPanel size="roomy"
   * sizeOverrides={{ CIRCLE_SIZE: 72 }} />
   */
  sizeOverrides,
}) => {
  const sz = useMemo(() => {
    const base = SIZE_PRESETS[size] || SIZE_PRESETS.compact;
    return { ...base, ...(sizeOverrides || {}) };
  }, [size, sizeOverrides]);

  const graphs = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(
      (g) => g?.graph?.nodes?.length > 0 && Array.isArray(g?.graph?.links),
    );
  }, [data]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showSummary, setShowSummary] = useState(false);

  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setActiveIndex(0);
    setProgress(0);
    setShowSummary(false);
  }, [graphs]);

  useEffect(() => {
    setShowSummary(false);
  }, [activeIndex]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () =>
      setDims({ width: el.clientWidth, height: el.clientHeight });
    update();
    const obs = new ResizeObserver(update);
    obs.observe(el);
    return () => obs.disconnect();
  }, [graphs]);

  const shouldAutoplay = isPlaying && !showSummary;

  useEffect(() => {
    if (!graphs || graphs.length <= 1 || !shouldAutoplay) {
      setProgress(0);
      return;
    }
    const startTime = Date.now();
    let rafId;
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(elapsed / AUTO_ADVANCE_MS, 1);
      setProgress(p);
      if (p >= 1) setActiveIndex((i) => (i + 1) % graphs.length);
      else rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [graphs, shouldAutoplay, activeIndex]);

  const pauseAutoplay = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handlePrev = useCallback(() => {
    if (!graphs || graphs.length <= 1) return;
    setActiveIndex((i) => (i - 1 + graphs.length) % graphs.length);
    setProgress(0);
  }, [graphs]);
  const handleNext = useCallback(() => {
    if (!graphs || graphs.length <= 1) return;
    setActiveIndex((i) => (i + 1) % graphs.length);
    setProgress(0);
  }, [graphs]);
  const togglePlay = useCallback(() => setIsPlaying((p) => !p), []);
  const toggleSummary = useCallback(() => setShowSummary((s) => !s), []);

  const currentGraph = graphs[activeIndex];
  const hasMultiple = graphs.length > 1;
  const hasReport =
    currentGraph?.report && currentGraph.report.trim().length > 0;

  const layoutData = useMemo(() => {
    if (!currentGraph?.graph) return null;
    return layoutGraph(currentGraph.graph, sz);
  }, [currentGraph, sz]);

  const progressBarColor = dark ? "bg-velBlue" : "bg-mutedText";
  const btnSizeClass = sz.CONTROL_BTN;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden font-poppins font-normal">
      <div className="flex shrink-0 items-start gap-3 border-b border-mainDivider px-4 py-2">
        {currentGraph?.title && (
          <p
            className="flex-1 font-poppins font-normal leading-snug text-mainText"
            style={{
              fontSize: sz.TITLE_PX,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {currentGraph.title}
          </p>
        )}
        {currentGraph?.timestamp && (
          <span className="shrink-0 font-poppins text-[10px] text-mutedText">
            {relativeTime(currentGraph.timestamp)}
          </span>
        )}
      </div>

      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        {!data ? (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-poppins text-[11px] text-mutedText">
              Loading graph…
            </span>
          </div>
        ) : graphs.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-poppins text-[11px] text-mutedText">
              No graph data available
            </span>
          </div>
        ) : layoutData && dims.width > 0 && dims.height > 0 ? (
          <>
            <PanZoomStage
              key={activeIndex}
              containerWidth={dims.width}
              containerHeight={dims.height}
              contentWidth={layoutData.contentWidth}
              contentHeight={layoutData.contentHeight}
              onInteract={pauseAutoplay}
              dark={dark}
            >
              <Graph layout={layoutData} dark={dark} sz={sz} />
            </PanZoomStage>

            {showSummary && currentGraph && (
              <SummaryOverlay
                report={currentGraph.report}
                onClose={() => setShowSummary(false)}
                dark={dark}
                size={size}
              />
            )}
          </>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-mainDivider">
        {hasMultiple && (
          <div className="relative h-0.5 w-full bg-mainDivider/50">
            <div
              className={`absolute left-0 top-0 h-full ${progressBarColor}`}
              style={{
                width: `${progress * 100}%`,
                transition: progress === 0 ? "none" : "width 50ms linear",
              }}
            />
          </div>
        )}
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            {graphs.length > 0 && (
              <span className="font-poppins font-mono text-[10px] text-mutedText">
                {activeIndex + 1} / {graphs.length}
              </span>
            )}
            {hasMultiple && shouldAutoplay && (
              <span className="font-poppins font-mono text-[10px] text-mutedText">
                {Math.max(
                  0,
                  Math.ceil(
                    (AUTO_ADVANCE_MS - progress * AUTO_ADVANCE_MS) / 1000,
                  ),
                )}
                s
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasMultiple && (
              <>
                <button
                  onClick={handlePrev}
                  className={`${btnSizeClass} flex items-center justify-center rounded-lg text-mutedText transition cursor-pointer hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2`}
                  title="Previous graph"
                >
                  <ChevronLeft
                    size={sz.CONTROL_ICON}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
                <button
                  onClick={togglePlay}
                  className={`${btnSizeClass} flex items-center justify-center rounded-lg transition cursor-pointer hover:bg-btnHover outline-none ring-inputFocus/60 focus-visible:ring-2 ${
                    isPlaying
                      ? dark
                        ? "text-velBlue"
                        : "text-mainText"
                      : "text-mutedText hover:text-mainText"
                  }`}
                  title={isPlaying ? "Pause autoplay" : "Resume autoplay"}
                >
                  {isPlaying ? (
                    <Pause
                      size={sz.CONTROL_ICON - 1}
                      strokeWidth={2}
                      aria-hidden
                    />
                  ) : (
                    <Play
                      size={sz.CONTROL_ICON - 1}
                      strokeWidth={2}
                      aria-hidden
                    />
                  )}
                </button>
                <button
                  onClick={handleNext}
                  className={`${btnSizeClass} flex items-center justify-center rounded-lg text-mutedText transition cursor-pointer hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2`}
                  title="Next graph"
                >
                  <ChevronRight
                    size={sz.CONTROL_ICON}
                    strokeWidth={2}
                    aria-hidden
                  />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasReport && (
              <button
                onClick={toggleSummary}
                className={`flex items-center gap-1.5 rounded-lg border px-2 text-[10px] font-medium transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2 ${
                  size === "roomy" ? "h-8 text-[11px]" : "h-6"
                } ${
                  showSummary
                    ? dark
                      ? "border-velBlue/50 bg-velBlue/[0.12] text-velBlue"
                      : "border-surfaceBorder bg-btnHover text-mainText"
                    : "border-inputBorder text-mutedText hover:border-surfaceBorder hover:bg-btnHover hover:text-mainText"
                }`}
                title={showSummary ? "Hide summary" : "Show summary"}
                aria-pressed={showSummary}
              >
                <FileText size={11} strokeWidth={2} aria-hidden />
                {showSummary ? "Hide Summary" : "Show Summary"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVEGraphPanel;
